<template>
  <ul class="flex flex-col">
    <li
      v-for="(group, groupIndex) in groups"
      :key="group.type"
      class="text-base"
      :class="{ 'border-t border-grey-light-01': groupIndex > 0 }"
    >
      <h2 class="p-16 text-lg uppercase md:p-32 md:py-24">
        {{ group.label }}
        ({{ group.count }})
      </h2>

      <ul>
        <StoreFinderItem
          v-for="(store, storeIndex) in group.items"
          :key="storeIndex"
          :is-active="
            activeStoreId === store.id &&
            actualGroupId === groupIndex.toString()
          "
          :group-id="groupIndex"
          v-bind="store"
        />
      </ul>
    </li>
  </ul>
</template>

<script lang="ts" setup>
import {
  DistributorType,
  type DistributorSearchItemFragment,
} from '#graphql-operations'

type StoreFinderGroup = {
  type: DistributorType
  count: number
  label: string
  items: DistributorSearchItemFragment[]
}

const props = withDefaults(
  defineProps<{
    countBoutique?: number
    countServiceCenter?: number
    countStore?: number
    items?: DistributorSearchItemFragment[]
    activeStoreId?: string
    activeGroupId?: string
  }>(),
  {
    countBoutique: 0,
    countServiceCenter: 0,
    countStore: 0,
    items: () => [],
    activeStoreId: undefined,
    activeGroupId: undefined,
  },
)

const { $texts } = useNuxtApp()

const groupLabels = computed<Record<DistributorType, string>>(() => ({
  [DistributorType.Own_boutique]: $texts(
    'storefinder.orisBoutique',
    'Oris Boutique',
  ),
  [DistributorType.Store]: $texts('storefinder.retailer', 'Retailer'),
  [DistributorType.Service_center]: $texts('storefinder.service', 'Service'),
  [DistributorType.Legacy]: $texts('storefinder.legacy', 'Retailer'),
}))

const groups = computed(() => {
  return Object.values(
    props.items.reduce<Partial<Record<DistributorType, StoreFinderGroup>>>(
      (acc, item) => {
        const type = item.distributorType
        if (!type) {
          return acc
        }
        if (!acc[type]) {
          acc[type] = {
            type,
            count: getCount(type),
            label: groupLabels.value[type],
            items: [],
          }
        }

        acc[type]!.items.push(item)
        return acc
      },
      {},
    ),
  )
})

const getCount = (type: DistributorType): number => {
  if (type === DistributorType.Service_center) {
    return props.countServiceCenter
  } else if (type === DistributorType.Store) {
    return props.countStore
  } else if (type === DistributorType.Own_boutique) {
    return props.countBoutique
  }

  return 0
}

const actualGroupId = computed(() => {
  return props.activeGroupId
    ? props.activeGroupId
    : groups.value
        .findIndex((group) =>
          group.items.some((store) => store.id === props.activeStoreId),
        )
        .toString()
})
</script>
